import React, { useEffect, useState } from 'react';
import SVGColorBlock from '../SVGColorBlock';
import { askJules } from './api';
import { resolveSuggestionColors } from './resolveSuggestionColors';
import julesIcon from '../simulcasticon.svg';

function JulesIcon({ spinning = false, className = 'h-5 w-5' }) {
	return (
		<img
			src={julesIcon}
			alt=""
			aria-hidden="true"
			className={`${className} ${spinning ? 'animate-spin' : ''}`.trim()}
		/>
	);
}

/**
 * Explicit Ask Jules panel for SceneEditor.
 * Independent prompt → Jules names the scene, invents colors, and returns a quip.
 */
export default function AskJulesPanel({
	system,
	numZones = 4,
	onApply,
	enabled = true,
}) {
	const [prompt, setPrompt] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [result, setResult] = useState(null);

	useEffect(() => {
		setResult(null);
		setError(null);
		setPrompt('');
	}, [system?.uid]);

	if (!enabled) return null;

	const trimmed = prompt.trim();
	const canAsk = trimmed.length >= 2 && !loading && !!system?.uid;

	const handleAsk = async () => {
		if (!canAsk) return;
		setLoading(true);
		setError(null);
		try {
			const data = await askJules({
				systemId: system.uid,
				prompt: trimmed,
				timezone: system.timezone,
				locationLabel: system.location || '',
				numZones,
			});
			setResult(data);
		} catch (err) {
			setResult(null);
			setError(err.message || 'Jules could not answer');
		} finally {
			setLoading(false);
		}
	};

	const handleApply = () => {
		if (!result || !onApply) return;
		const resolved = resolveSuggestionColors(
			{
				name: result.name,
				colors: result.colors,
				expertMethodId: result.expertMethodId,
			},
			numZones
		);
		onApply({
			name: result.name,
			...resolved,
		});
	};

	return (
		<div className="rounded-xl border border-[#880e4f]/30 bg-[#880e4f]/5 dark:bg-[#880e4f]/10 p-3 space-y-3">
			<div className="flex items-center gap-2 text-sm font-medium text-[#880e4f]">
				<JulesIcon spinning={loading} className="h-5 w-5" />
				<span>Ask Jules</span>
			</div>
			<p className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
				Describe a look, event, or mood — dates in plain text are fine. Jules will name the scene and pick colors.
			</p>

			<textarea
				rows={2}
				className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-3 py-2 w-full text-sm resize-y min-h-[2.75rem]"
				value={prompt}
				onChange={(e) => setPrompt(e.target.value)}
				placeholder="e.g. Canada Day fireworks downtown, July 1"
				disabled={loading}
			/>

			<button
				type="button"
				disabled={!canAsk}
				onClick={handleAsk}
				className="w-full px-3 py-2 text-sm rounded-xl bg-[#880e4f] text-white hover:bg-[#6b0b3f] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
			>
				<JulesIcon spinning={loading} className="h-4 w-4 brightness-0 invert" />
				{loading ? 'Jules is thinking…' : 'Ask Jules'}
			</button>

			{error && (
				<p className="text-xs text-red-600 dark:text-red-400">{error}</p>
			)}

			{result && (
				<div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 space-y-2">
					{result.name && (
						<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={result.name}>
							{result.name}
						</div>
					)}
					{result.message && (
						<p className="text-sm text-gray-700 dark:text-gray-200 italic">
							{result.message}
						</p>
					)}
					<div className="flex items-center gap-3">
						{result.colors?.length > 0 && (
							<SVGColorBlock size={44} data={result.colors} />
						)}
						<div className="flex-1 min-w-0">
							<div className="text-[10px] text-gray-400">
								{result.colorSource === 'expert' && result.expertMethodId
									? `expert: ${result.expertMethodId}`
									: 'AI palette'}
							</div>
						</div>
						<button
							type="button"
							onClick={handleApply}
							className="text-[11px] px-2.5 py-1.5 rounded-lg border border-[#880e4f]/40 text-[#880e4f] hover:bg-[#880e4f]/10 flex-shrink-0"
						>
							Use name &amp; colors
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export { JulesIcon };
